




























































import Vue from 'vue';
import type { PropOptions } from 'vue';
import { email, minLength, required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import BravadoTheme from '~/components/common/BravadoTheme/BravadoTheme.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import axios, { AxiosError } from 'axios';
import { getValidReferrer } from '../utils';
import type { UserFormFields } from './types';

export default Vue.extend({
  name: 'SignUpForm',
  components: {
    BravadoTheme,
    BravadoButtonV3,
    BravadoTextInputV3,
  },
  mixins: [validationMixin],
  props: {
    promo: {
      type: String,
    } as PropOptions<string>,
    referer: {
      type: String,
    } as PropOptions<string>,
    authSource: {
      type: String,
    } as PropOptions<string>,
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
        firstname: '',
        lastname: '',
      },
      attemptedSubmission: false,
      isValid: true,
      isLoading: false,
    };
  },
  validations: {
    form: {
      email: { required, email },
      password: { required, minLength: minLength(6) },
      firstname: { required },
      lastname: { required },
    },
  },
  methods: {
    /**
     * Sign up by form with user/password for WebView (linkedin)
     */
    async onFormSignup() {
      if (this.$v.form.$invalid) {
        this.attemptedSubmission = true;
        return;
      }

      const { query } = this.$route;
      this.isLoading = true;
      const user = this.form as UserFormFields;

      try {
        await this.$axios.$post('/api/users.json', {
          user: {
            ...user,
            password_confirmation: user.password,
            promo: this.promo,
            redirect_path: this.$pages.WAR_ROOM_POSTS_TAB_MYFEED_PAGE.path,
          },
        });

        await this.$auth.fetchUser();

        // await this.$store.dispatch('portfolio/load', { slug: this.$auth.user.slug });
        this.$analytics.logSignUp(
          'email',
          this.authSource ? { auth_source: this.authSource } : {},
          this.$auth.user!.amplitudeUser,
        );
        if (query?.event) {
          this.$analytics.logEvent(query.event as string);
        }

        // Do we still use `referer` cookie?
        const referer = this.referer || this.$cookies.get('referer');
        if (getValidReferrer(referer)) {
          this.$cookies.remove('referer');
          await this.$3rdParty.loadImmediately(['facebook', 'tiktok', 'linkedin']);
          window.location.href = referer;
        } else {
          // We use window.location.href instead of $router because of the bug in WebView:
          // Page could redirect the user, but user state wasn't updated somehow
          // and app shows SignUp modal again
          window.location.href = '/onboarding-v3/';
        }
      } catch (e) {
        // We don't remove isLoading in success state because of the issue in WebView:
        // On mobile phone next page could loads too long - user might click button again
        this.isLoading = false;

        // Server returns 400 if email is already taken
        if (axios.isAxiosError(e) && e.response?.status === 400) {
          this.$notice.error('This email is already taken. Please Log in.');
          return;
        }

        // TODO: server should have a specific messages for each error
        this.$notice.error('Something unexpected happened. We are working on it. Please try again later.');
        this.$sentryUtils.error(e as Error, 'webview_signup');
      }
    },

    isFieldValid(fieldName: string) {
      const formElement = this.$v.form[fieldName]!;
      return !(this.attemptedSubmission && formElement.$invalid) && this.isValid;
    },
  },

  computed: {
    passwordErrorMessage(): string {
      if (this.isFieldValid('password')) return '';
      if (!this.$v.form.password?.required) return 'Password is required';
      if (!this.$v.form.password.minLength) return 'Password must be at least 6 characters long';

      return '';
    },
    emailErrorMessage(): string {
      if (this.isFieldValid('email')) return '';
      if (!this.$v.form.email?.required) return 'Email is required';
      if (!this.$v.form.email.email) return 'Please provide a valid email';

      return '';
    },
    lastnameErrorMessage(): string {
      return this.isFieldValid('firstname') ? '' : 'Last name is required';
    },
    firstnameErrorMessage(): string {
      return this.isFieldValid('lastname') ? '' : 'First name is required';
    },
  },

  watch: {
    // Save email to prefill on log in or restore
    'form.email'(value: string) {
      try {
        sessionStorage.setItem('email', value?.trim());
      } catch (e) {
        this.$sentryUtils.error(e);
      }
    },
  },
});
